/**
 * @file Onglet « Familiers » de l'ecran Skins : achat en Pieces de familier (gagnees en mode Monstres), equipement sur 3 emplacements,
 * bonus permanents tant que le familier est equipe. C'est le seul debouche de cette monnaie (voir docs/ECONOMIE.md).
 */
import type { ModifierDef, PetDef } from '@config/types';
import { stageValueToDisplay } from '@core/index';
import { Decimal } from '@shared/bignum';
import type { WalletId } from '@shared/enums';
import { formatBig } from '@shared/format';
import { Component } from '../../core/Component';
import { h, setText, toggleClass } from '../../core/dom';
import { send } from '../../core/feedback';
import { currencyIcon } from '../../core/icons';
import { balanceOf, walletChips } from '../../core/widgets';
import type { UiContext } from '../../UiContext';

interface CardView {
  pet: PetDef;
  root: HTMLElement;
  state: HTMLElement;
  costs: HTMLElement;
}

const percent = (m: ModifierDef): string => {
  const v = Decimal.fromValue(m.value).toNumber();
  return `${Math.round((m.op === 'mult' ? v - 1 : v) * 1000) / 10}%`;
};

export class PetsPane extends Component {
  private readonly cards: CardView[] = [];
  private lockNote!: HTMLElement;
  private slots!: HTMLElement;
  private balance!: HTMLElement;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'pets-pane');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    const grid = h('div', { class: 'aura-grid' });
    for (const pet of this.ctx.config.pets) {
      const grade = this.ctx.config.grades.find((g) => g.id === pet.grade);
      const bonus = pet.bonus.map((b) => (b.stat === 'moveSpeed' ? `${t.tDyn(`stat.${b.stat}`)} +${Decimal.fromValue(b.value).toNumber()}` : `${t.tDyn(`stat.${b.stat}`)} +${percent(b)}`));
      const state = h('div', { class: 'skin-state' });
      const costs = h('div', { class: 'skin-costs' }, ...walletChips(this.ctx, pet.cost));
      const root = h(
        'button',
        { class: 'skin-card pet-card', type: 'button', 'data-ui-block': true, 'data-pet': pet.id, onclick: () => this.activate(pet) },
        h('div', { class: 'aura-view pet-view', style: `--grade:${grade?.colors.primary ?? '#999'}` }, h('span', { class: 'pet-grade' }, pet.grade)),
        h('div', { class: 'skin-name' }, t.name(pet.name)),
        h('div', { class: 'skin-bonus' }, bonus.join(' · ')),
        state,
        costs,
      );
      this.cards.push({ pet, root, state, costs });
      grid.append(root);
    }
    this.balance = h('b', {});
    this.slots = h('b', {});
    const rule = this.ctx.config.featureUnlocks.pets;
    const at = rule.kind === 'stageValue' ? stageValueToDisplay(this.ctx.config.balance, rule.atLeast).stage : 1;
    this.lockNote = h('p', { class: 'hint pet-lock' }, `${t.t('common.unlockAt', { stage: at })} · ${t.t('pets.locked')}`);
    this.el.append(
      h('p', { class: 'hint' }, t.t('pets.hint')),
      h('div', { class: 'chips-row' }, h('span', { class: 'chip' }, currencyIcon('petCoin'), ' ', this.balance), h('span', { class: 'chip' }, t.t('pets.slots'), ' ', this.slots)),
      this.lockNote,
      grid,
    );
    this.update(0);
  }

  private activate(pet: PetDef): void {
    const { data } = this.ctx.sim;
    if (!data.pets.owned[pet.id]) {
      void send(this.ctx, { type: 'pet.buy', petId: pet.id });
      return;
    }
    if (data.pets.equipped.includes(pet.id)) {
      void send(this.ctx, { type: 'pet.unequip', petId: pet.id });
      return;
    }
    // emplacements pleins : le nouveau remplace le dernier equipe
    const max = this.ctx.config.balance.pets.maxEquipped;
    void send(this.ctx, { type: 'pet.equip', petId: pet.id, slot: Math.min(data.pets.equipped.length, max - 1) });
  }

  override update(_dt: number): void {
    const { data, query } = this.ctx.sim;
    const { i18n } = this.ctx;
    const unlocked = query.feature('pets');
    toggleClass(this.lockNote, 'is-hidden', unlocked);
    setText(this.balance, formatBig(balanceOf(this.ctx, 'petCoin')));
    setText(this.slots, `${data.pets.equipped.length}/${this.ctx.config.balance.pets.maxEquipped}`);
    for (const c of this.cards) {
      const owned = data.pets.owned[c.pet.id] !== undefined;
      const equipped = data.pets.equipped.includes(c.pet.id);
      toggleClass(c.root, 'owned', owned);
      toggleClass(c.root, 'equipped', equipped);
      toggleClass(c.costs, 'is-hidden', owned);
      setText(c.state, equipped ? i18n.t('common.equipped') : owned ? i18n.t('common.equip') : '');
      const cost = c.pet.cost;
      const affordable = (Object.keys(cost) as WalletId[]).every((id) => {
        const v = cost[id];
        return v === undefined || balanceOf(this.ctx, id).gte(Decimal.fromValue(v));
      });
      toggleClass(c.root, 'disabled', !owned && (!affordable || !unlocked));
    }
  }
}
